import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { reaction } from "mobx";
import { observer } from "mobx-react-lite";
import React from "react";
import { View, YStack } from "tamagui";
import { useApi } from "../core/apis";
import { useLoadUsersInPlan } from "../core/hooks/useLoadUsersInPlan";
import { useStores } from "../core/stores";
import { usePerformance } from "../core/usePerformance";
import { Footer } from "../ui/Footer";
import { FullScreenLoading } from "../ui/Loading";
import { Screen } from "../ui/Screen";
import { AvatarSize, UserAvatar } from "../ui/UserAvatar";
import { PlanQuickstartUI } from "./PlanQuickstartUI";
import { usePlanNavigation } from "./usePlanNavigation";

type Props =
  | NativeStackScreenProps<ReactNavigation.RootParamList, "Plan">
  | NativeStackScreenProps<ReactNavigation.RootParamList, "Module">;

function useHasLoadedAlready(data: boolean) {
  const [hasLoaded, setHasLoaded] = React.useState(data);

  React.useEffect(() => {
    if (!hasLoaded && data) {
      setHasLoaded(true);
    }
  }, [hasLoaded, data]);

  return hasLoaded;
}

export function useUserSelectForPlan(
  planUid: string,
  moduleUid?: string,
  dataUid?: string,
) {
  const { plansStore } = useStores();
  const plan = plansStore.byId.get(planUid);
  const { replace } = usePlanNavigation();

  const triggerFlow = plan?.isUsersLoaded && plan?.userInPlan === undefined;
  React.useEffect(() => {
    if (triggerFlow) {
      replace("UserSelect", {
        moduleUid: moduleUid,
        dataUid: dataUid,
        planUid: planUid,
      });
    }
  }, [triggerFlow, replace, planUid, dataUid, moduleUid]);

  return null;
}

export const PlanScreen = observer(({ route, navigation }: Props) => {
  const planUid = route.params.planUid;

  const { plansStore, sessionStore } = useStores();
  const { plansApi } = useApi();
  const { start, stop } = usePerformance("PLAN_TRACE");

  const plan = plansStore.byId.get(planUid);

  useUserSelectForPlan(planUid);
  useLoadUsersInPlan(planUid);

  React.useEffect(() => {
    navigation.setOptions({
      headerRight: () =>
        plan?.userInPlan && (
          <View marginRight="$medium">
            <UserAvatar
              size={AvatarSize.Medium}
              withLink
              userUid={plan?.userInPlan.uid}
            />
          </View>
        ),
    });
  }, [navigation, plan?.userInPlan]);

  React.useEffect(() => {
    if (plan && !plan.planHasAllDataLoaded) {
      start();
      return reaction(
        () => plan.planHasAllDataLoaded,
        (isLoadedNow) => {
          if (isLoadedNow) {
            stop({
              modules: `${plan.moduleCount}`,
              sections: `${plan.data.sectionIds.length}`,
              users: `${plan.data.userIds.length}`,
            });
          }
        },
      );
    }
  }, [start, stop, plan]);

  // Update user -> if email found in auth but not in user
  React.useEffect(() => {
    const user = plan?.userInPlan;
    if (user && !user.data.email && sessionStore.authUser?.email) {
      void user.updateEmail(sessionStore.authUser?.email);
    }
  }, [plan, sessionStore]);

  const hasLoadedAlready = useHasLoadedAlready(plan?.isUsersLoaded ?? false);

  React.useEffect(() => {
    if (sessionStore.isLoggedIn) {
      const unsub = plansApi.watchItem(planUid);
      return () => {
        unsub();
      };
    }
  }, [plansApi, planUid, sessionStore.isLoggedIn]);

  if (!plan || !hasLoadedAlready || !sessionStore.isLoggedIn) {
    return <FullScreenLoading subtitle={"Planira loading..."} />;
  }

  return (
    <Screen>
      <YStack gap="$medium">
        <PlanQuickstartUI uid={planUid} />
      </YStack>
      <Footer />
    </Screen>
  );
});

export default PlanScreen;

// Skipped for now
// <PushEnableBox planUid={planUid} />
